import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import RtlButton from './RtlButton';
import { useDialogs } from '../hooks/useDialogs/useDialogs';
import { useNotification } from './CustomNotification';
import {
  getMany as getCountries,
  deleteOne as deleteCountry,
  createOne as createCountry,
  updateOne as updateCountry,
  type Country,
} from '../data/countries';
import PageContainer from './PageContainer';
import { useLanguage } from '../../contexts/LanguageContext';
import CountryFormDialog from './CountryFormDialog';

export default function CountryList() {
  const { t, direction } = useLanguage();
  const dialogs = useDialogs();
  const notifications = useNotification();

  const [countries, setCountries] = React.useState<Country[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedCountry, setSelectedCountry] = React.useState<Country | null>(null);

  const isRtl = direction === 'rtl';

  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await getCountries({
        sort_by: 'name',
        sort_order: 'asc',
        per_page: 100,
      });

      setCountries(result.data || []);
    } catch (loadError) {
      setError(loadError as Error);
    }

    setIsLoading(false);
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = React.useCallback(() => {
    if (!isLoading) {
      loadData();
    }
  }, [isLoading, loadData]);

  const handleCreateClick = React.useCallback(() => {
    setSelectedCountry(null);
    setDialogOpen(true);
  }, []);

  const handleEditClick = React.useCallback((country: Country) => {
    setSelectedCountry(country);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = React.useCallback(() => {
    setDialogOpen(false);
    setSelectedCountry(null);
  }, []);

  const handleSave = React.useCallback(
    async (name: string) => {
      try {
        if (selectedCountry) {
          await updateCountry(selectedCountry.id, { name });
          notifications.show(t('countries.updateSuccess'), {
            severity: 'success',
            autoHideDuration: 3000,
          });
        } else {
          await createCountry({ name });
          notifications.show(t('countries.createSuccess'), {
            severity: 'success',
            autoHideDuration: 3000,
          });
        }
        loadData();
      } catch (saveError) {
        throw saveError;
      }
    },
    [selectedCountry, loadData, notifications, t],
  );

  const handleDelete = React.useCallback(
    (country: Country) => async () => {
      const confirmed = await dialogs.confirm(
        t('countries.deleteConfirm', { name: country.name }),
        {
          title: t('countries.deleteTitle'),
          severity: 'error',
          okText: t('common.delete'),
          cancelText: t('common.cancel'),
        },
      );

      if (confirmed) {
        setIsLoading(true);
        try {
          await deleteCountry(country.id);
          notifications.show(t('countries.deleteSuccess'), {
            severity: 'success',
            autoHideDuration: 3000,
          });
          loadData();
        } catch (deleteError) {
          notifications.show(
            `${t('messages.errorOccurred')}: ${(deleteError as Error).message}`,
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
          setIsLoading(false);
        }
      }
    },
    [dialogs, notifications, loadData, t],
  );

  const pageTitle = t('countries.title');

  return (
    <PageContainer
      breadcrumbs={[{ title: pageTitle }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={t('common.refresh')} placement="bottom" enterDelay={1000}>
            <div>
              <IconButton
                size="small"
                aria-label="refresh"
                onClick={handleRefresh}
                sx={{ marginLeft: isRtl ? 1 : 0 }}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <RtlButton
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
            sx={{
              background: '#028bcb !important',
              boxShadow: 'unset !important',
              border: '0 !important',
              '&:hover': { background: '#0277a8 !important' },
            }}
          >
            {t('common.create')}
          </RtlButton>
        </Stack>
      }
    >
      <Box sx={{ width: '100%' }}>
        {error ? (
          <Alert severity="error">{error.message}</Alert>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {countries.map((country) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={country.id}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s',
                    '&:hover': {
                      elevation: 4,
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {country.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={t('common.edit')}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(country)}
                        sx={{
                          color: '#028bcb',
                          '&:hover': {
                            backgroundColor: 'rgba(2, 139, 203, 0.1)',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                      <IconButton
                        size="small"
                        onClick={handleDelete(country)}
                        sx={{
                          color: '#f44336',
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {!isLoading && !error && countries.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {t('countries.noCountries')}
            </Typography>
          </Box>
        )}
      </Box>

      <CountryFormDialog
        open={dialogOpen}
        country={selectedCountry}
        onClose={handleDialogClose}
        onSave={handleSave}
      />
    </PageContainer>
  );
}

